import apiClient, { apiRequest } from "./apiClient";

/**
 * Get Provider API Catalog with test fixtures
 * Returns the provider_api header + all active endpoints with their default test fixtures
 * Calls: GET /api/v1/test-execution/provider-apis/{provider_api_id}/catalog
 * @param {string} providerApiId - UUID of the provider API
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const getProviderApiCatalog = async (providerApiId) => {
  return apiRequest(
    () =>
      apiClient.get(
        `/api/v1/test-execution/provider-apis/${providerApiId}/catalog`
      ),
    "Failed to fetch provider API catalog"
  );
};

/**
 * List fixtures for a specific endpoint
 * Calls: GET /api/v1/test-execution/endpoints/{endpoint_id}/fixtures
 * @param {string} endpointId - UUID of the endpoint
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const listEndpointFixtures = async (endpointId) => {
  return apiRequest(
    () =>
      apiClient.get(`/api/v1/test-execution/endpoints/${endpointId}/fixtures`),
    "Failed to fetch endpoint fixtures"
  );
};

/**
 * Execute a test request
 * Execute an endpoint in test mode using the default fixture, a named fixture, or a caller-supplied payload
 * Secrets are read from orbit.test_secrets by provider_name
 * Calls: POST /api/v1/test-execution/execute
 * @param {Object} testRequest - Test execution request data
 *   - endpoint_id: string (UUID)
 *   - fixture_name?: string (optional)
 *   - payload?: Object (optional)
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const executeTest = async (testRequest) => {
  return apiRequest(
    () => apiClient.post(`/api/v1/test-execution/execute`, testRequest),
    "Failed to execute test"
  );
};

/**
 * Download a binary endpoint (e.g. a documents .zip) as a file blob.
 * Streams the raw body through Orbit's passthrough route with the current
 * parameters (path/query keys like "path.application_id" go in the query string).
 * Calls: GET /api/v1/execute-endpoint/{endpoint_id}/download
 * @param {string} endpointId - UUID of the endpoint
 * @param {Object} parameters - schema-keyed params (e.g. {"path.application_id": "..."})
 * @returns {Promise<import("axios").AxiosResponse<Blob>>} raw axios response (blob + headers)
 */
export const downloadEndpointFile = async (endpointId, parameters) => {
  return apiClient.get(`/api/v1/execute-endpoint/${endpointId}/download`, {
    params: parameters || {},
    responseType: "blob",
    timeout: 120000
  });
};
